import React from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { ShoppingBag, Check } from 'lucide-react';
import { toast } from 'react-toastify';
import { api } from '../services/api';

/**
 * Friday's treatment order on the dashboard: every bag and bottle still booked
 * Saturday through next Friday, off the Plan Cost Sheet. Checking it off means
 * he bought it — next Friday it comes back.
 */
const money = (n: number) => `$${Math.round(Number(n) || 0).toLocaleString('en-US')}`;

type Line = { key: string; name: string; word: string; amount: number; unit: string; packs: number; pack: string; cost: number };
type Buy = {
  friday: string; from: string; to: string; shopping: boolean; bought: boolean;
  visits: number; lines: Line[]; cost: number; title: string; range: string; key: string;
};

export default function PlanBuyCard() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<Buy>('plan-friday-buy', () => api.get('/plan-sheet/buy').then(r => r.data), { staleTime: 60_000 });
  const mark = useMutation(
    (key: string) => api.post(`/attention/${encodeURIComponent(key)}/done`).then(r => r.data),
    {
      onSuccess: (r: any) => { toast.success(r?.did || 'Marked bought'); qc.invalidateQueries('plan-friday-buy'); qc.invalidateQueries('attention'); },
      onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not mark that'); },
    }
  );
  if (isLoading || !data) return null;
  if (!data.lines.length || data.bought) return null;

  const shop = data.shopping;
  return (
    <section className={`rounded-2xl border-2 p-5 ${shop ? 'border-brand-600 bg-brand-50' : 'border-gray-200 bg-white'}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className={`text-[11px] font-bold uppercase tracking-widest ${shop ? 'text-brand-700' : 'text-gray-500'}`}>
            {shop ? 'Friday shop' : "Friday's order"}
          </p>
          <h2 className="text-xl font-bold text-gray-900 mt-0.5 leading-tight">{data.title}</h2>
          <p className="text-sm text-gray-600 mt-1">
            {data.range} · {data.visits} visit{data.visits === 1 ? '' : 's'} on the plans. Bags and bottles with waste, SiteOne / Court Hardware.
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-3xl font-bold tabular-nums text-gray-900">{money(data.cost)}</p>
          <p className="text-xs text-gray-500">to buy</p>
        </div>
      </div>

      <ul className="mt-4 divide-y divide-gray-200/80 rounded-xl border border-gray-200 bg-white overflow-hidden">
        {data.lines.map(l => (
          <li key={l.key} className="px-4 py-2.5 flex items-baseline justify-between gap-3">
            <span className="min-w-0">
              <span className="block text-[15px] font-semibold text-gray-900">
                {l.unit === 'visit' || l.unit === 'each' ? `${l.packs} ${l.word}` : `${l.packs} × ${l.pack}`}
                <span className="font-medium text-gray-700"> — {l.name}</span>
              </span>
              <span className="block text-xs text-gray-500">{l.word} · {l.amount} {l.unit} with waste</span>
            </span>
            <span className="text-sm font-semibold tabular-nums text-gray-800 flex-shrink-0">{money(l.cost)}</span>
          </li>
        ))}
      </ul>

      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <button type="button" onClick={() => mark.mutate(data.key)} disabled={mark.isLoading}
          className="press inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 text-black text-sm font-semibold hover:bg-brand-500 disabled:opacity-50">
          <Check className="h-4 w-4" /> {mark.isLoading ? 'Saving…' : 'I bought this'}
        </button>
        <Link to="/treatments" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700">
          <ShoppingBag className="h-4 w-4" /> Treatments
        </Link>
      </div>
    </section>
  );
}